import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Brain, Download, Trophy, TrendingUp, Users, Home, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { calculateIQScore, getPercentile, getIQCategory } from '@/services/scoreService';
import { generatePDF } from '@/services/pdfService';

const ResultsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [animatedScore, setAnimatedScore] = useState(0);
  
  const { score, totalQuestions, level } = location.state || {};

  useEffect(() => {
    if (!score && score !== 0) {
      navigate('/');
      return;
    }

    // Animate score
    const iqScore = calculateIQScore(score, totalQuestions, level);
    let current = 0;
    const increment = iqScore / 50;
    const timer = setInterval(() => {
      current += increment;
      if (current >= iqScore) {
        setAnimatedScore(iqScore);
        clearInterval(timer);
      } else {
        setAnimatedScore(Math.floor(current));
      }
    }, 20);

    return () => clearInterval(timer);
  }, [score, totalQuestions, level, navigate]);

  if (!score && score !== 0) {
    return null;
  }

  const iqScore = calculateIQScore(score, totalQuestions, level);
  const percentile = getPercentile(iqScore);
  const category = getIQCategory(iqScore);
  const accuracy = Math.round((score / totalQuestions) * 100);

  const handleDownloadPDF = () => {
    generatePDF({
      iqScore,
      percentile,
      category,
      level: level || 'beginner',
      accuracy,
      score,
      totalQuestions,
    });
  };

  const handleRetakeTest = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-teal-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-purple-100 sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg gradient-purple-blue flex items-center justify-center">
                <Brain className="text-white" size={24} />
              </div>
              <div>
                <h1 className="text-xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">IQ Test Pro</h1>
                <p className="text-xs text-gray-600">Your Results</p>
              </div>
            </div>
            <Button variant="outline" onClick={handleRetakeTest}>
              <Home className="mr-2" size={18} />
              Home
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto"
        >
          {/* Congratulations Banner */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", duration: 0.6 }}
            >
              <Trophy className="w-20 h-20 mx-auto mb-4 text-yellow-500" />
            </motion.div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              Congratulations!
            </h1>
            <p className="text-xl text-gray-600">
              You've completed the {level} level IQ test
            </p>
          </div>

          {/* Main Score Card */}
          <Card className="p-8 md:p-12 mb-8 shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0 gradient-multi opacity-5"></div>
            <div className="relative">
              <div className="text-center mb-8">
                <p className="text-gray-600 mb-4 text-lg">Your IQ Score</p>
                <motion.div
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  className="text-8xl md:text-9xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent mb-4"
                >
                  {animatedScore}
                </motion.div>
                <div className="inline-block px-6 py-3 rounded-full gradient-purple-blue text-white text-xl font-semibold">
                  {category.label}
                </div>
              </div>

              <div className="space-y-6">
                {/* Percentile */}
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-6 rounded-2xl">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Users className="text-purple-600" size={24} />
                      <span className="font-semibold text-gray-900">Global Ranking</span>
                    </div>
                    <span className="text-3xl font-bold text-purple-600">{percentile}%</span>
                  </div>
                  <p className="text-gray-600 text-sm">
                    You scored better than {percentile}% of the global population
                  </p>
                  <Progress value={percentile} className="mt-3 h-3" />
                </div>

                {/* Accuracy */}
                <div className="bg-gradient-to-r from-teal-50 to-blue-50 p-6 rounded-2xl">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="text-teal-600" size={24} />
                      <span className="font-semibold text-gray-900">Accuracy</span>
                    </div>
                    <span className="text-3xl font-bold text-teal-600">{accuracy}%</span>
                  </div>
                  <p className="text-gray-600 text-sm">
                    {score} out of {totalQuestions} questions answered correctly
                  </p>
                  <Progress value={accuracy} className="mt-3 h-3" />
                </div>
              </div>
            </div>
          </Card>

          {/* IQ Distribution Chart */}
          <Card className="p-8 mb-8 shadow-xl">
            <h3 className="text-2xl font-bold mb-6 text-center text-gray-900">IQ Distribution</h3>
            <div className="space-y-4">
              {[
                { range: 'Above 140', label: 'Genius', percent: 0.5, color: 'bg-purple-500' },
                { range: '130-139', label: 'Very Superior', percent: 2, color: 'bg-blue-500' },
                { range: '120-129', label: 'Superior', percent: 7, color: 'bg-teal-500' },
                { range: '110-119', label: 'High Average', percent: 16, color: 'bg-green-500' },
                { range: '90-109', label: 'Average', percent: 50, color: 'bg-yellow-500' },
                { range: '80-89', label: 'Low Average', percent: 16, color: 'bg-orange-500' },
                { range: 'Below 80', label: 'Below Average', percent: 8.5, color: 'bg-red-500' },
              ].map((item, idx) => (
                <div key={idx} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-gray-700">
                      {item.label} ({item.range})
                    </span>
                    <span className="text-gray-600">{item.percent}%</span>
                  </div>
                  <div className="relative h-8 bg-gray-100 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${item.percent}%` }}
                      transition={{ duration: 1, delay: idx * 0.1 }}
                      className={`h-full ${item.color} flex items-center justify-end pr-2`}
                    >
                      {iqScore >= parseInt(item.range.split('-')[0]) && 
                       iqScore <= (parseInt(item.range.split('-')[1]) || 200) && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="w-6 h-6 bg-white rounded-full border-2 border-white shadow-lg"
                        >
                          <div className="w-full h-full rounded-full bg-gradient-to-br from-yellow-400 to-orange-500"></div>
                        </motion.div>
                      )}
                    </motion.div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Action Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Button
              onClick={handleDownloadPDF}
              size="lg"
              className="gradient-purple-blue h-14 text-lg"
            >
              <Download className="mr-2" size={20} />
              Download Certificate
            </Button>
            <Button
              onClick={handleRetakeTest}
              variant="outline"
              size="lg"
              className="h-14 text-lg"
            >
              <Home className="mr-2" size={20} />
              Take Another Test
            </Button>
          </div>

          {/* Share Section */}
          <Card className="p-6 mt-8 bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Share2 className="text-purple-600" size={24} />
                <div>
                  <h4 className="font-semibold text-gray-900">Share Your Results</h4>
                  <p className="text-sm text-gray-600">Let others know about your achievement</p>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default ResultsPage;
